/**
 * manual-registration.ts
 *
 * Shared, browser-safe plumbing for the user-initiated "register this query as a
 * data point" flow. It turns the flat form a UI / config-builder collects —
 * `{ connection, name, query, value_column, time_column?, cadence }` — into the
 * `{ connection, datapoint, db }` shapes the proxy `POST /ontology/tags/batch` endpoint
 * and the graph-identity MERGE consume.
 *
 * This is the ONE place the manual form -> wire mapping (datapoint slugging,
 * defaulting, validation) lives, so every caller agrees:
 *   - the api-service BFF route (`registerManualDbDataPoint`),
 *   - the DB workbench "save as data point" flow,
 *   - the config-builder "propose + register" flow.
 *
 * Pure and dependency-light (only sibling ontology-db helpers): it never talks
 * to the network — the transport (proxy batch + graph write) is the
 * registrar's job.
 */

import { buildDbTagPath } from './db-tag-path'
import { normalizeDbSyncMode, validateDbDatapoint } from './query'
import { type DbConnectionInput, type DbDatapointInput, type DbSyncMode } from './wire-types'

/**
 * The flat form a UI collects to register a single query as a data point.
 * `connection.name` + `connection.url` are required — `name` keys the `db://`
 * grammar and both graph URNs, `url` is the registry `source_url`.
 */
export interface ManualDbRegistrationInput {
  /** The database connection posture (url + name, optional transport/creds). */
  connection: DbConnectionInput
  /** Human display name for the data point; also the default datapoint slug. */
  name: string
  /** The read-only SELECT the data point runs. */
  query: string
  /** Result column the reader samples (empty = whole-row DataSet mode). */
  valueColumn?: string
  /** Timestamp column, required only for `incremental` sync. */
  timeColumn?: string
  /** Poll cadence in ms; omitted lets the proxy apply its default/floor. */
  cadenceMs?: number
  /** `sample` (default) or `incremental`. */
  syncMode?: DbSyncMode
  /**
   * Explicit `db://{connection}/{datapoint}` datapoint segment. When omitted it
   * is derived from `name` via {@link slugifyDatapoint}, so a caller that only
   * has a display name still gets a stable, grammar-safe path.
   */
  datapoint?: string
  /** Defaults to true (register live); false registers disabled. */
  enabled?: boolean
}

/**
 * The normalized registration a caller hands to the registrar: the
 * connection posture plus one resolved data point (grammar-safe `datapoint`
 * segment, validated `db` block, and the optional registry-row extras).
 */
export interface ManualDbRegistration {
  connection: DbConnectionInput
  datapoint: string
  db: DbDatapointInput
  tagPath: string
  displayLabel?: string
  frequencyMs?: number
  enabled?: boolean
}

export type ManualDbRegistrationResult =
  | { ok: true; registration: ManualDbRegistration }
  | { ok: false; error: string }

/**
 * Turn a free-form display name into a `db://` datapoint segment: lowercase,
 * non-alphanumeric runs collapse to a single underscore, and leading/trailing
 * underscores are trimmed. `/` is intentionally NOT preserved — a manual name
 * maps to a flat leaf; callers that want a `schema/table/col` datapoint pass an
 * explicit `datapoint`. Returns `''` for a name with no alphanumerics so the
 * caller can reject it rather than mint an empty path.
 */
export function slugifyDatapoint(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Build (and validate) a single manual db data-point registration. Returns
 * `{ ok: false, error }` for any invalid input (missing connection identity,
 * un-sluggable name, non-SELECT query, incremental sync without value/time
 * columns) so the caller can surface a 400 without ever reaching the proxy or
 * the graph. The query validation mirrors the proxy's `dbguard` /
 * `validateDBBlock` exactly (via `validateDbDatapoint`).
 */
export function buildManualDbRegistration(
  input: ManualDbRegistrationInput
): ManualDbRegistrationResult {
  const connectionName = input.connection?.name?.trim() ?? ''
  if (connectionName === '') {
    return { ok: false, error: 'connection.name is required' }
  }
  if ((input.connection?.url ?? '').trim() === '') {
    return { ok: false, error: 'connection.url is required' }
  }

  const displayName = input.name?.trim() ?? ''
  const explicitDatapoint = input.datapoint?.trim() ?? ''
  const datapoint = explicitDatapoint !== '' ? explicitDatapoint : slugifyDatapoint(displayName)
  if (datapoint === '') {
    return {
      ok: false,
      error: 'name (or datapoint) is required and must contain an alphanumeric character'
    }
  }

  let tagPath: string
  try {
    tagPath = buildDbTagPath(connectionName, datapoint)
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'invalid connection/datapoint' }
  }

  const syncMode = normalizeDbSyncMode(input.syncMode) as DbSyncMode
  const db: DbDatapointInput = {
    connection_name: connectionName,
    query: input.query,
    sync_mode: syncMode,
    ...(input.connection.transport ? { transport: input.connection.transport } : {}),
    ...(input.valueColumn?.trim() ? { value_column: input.valueColumn.trim() } : {}),
    ...(input.timeColumn?.trim() ? { time_column: input.timeColumn.trim() } : {})
  }

  const invalid = validateDbDatapoint(db)
  if (invalid) {
    return { ok: false, error: invalid }
  }

  const registration: ManualDbRegistration = {
    connection: input.connection,
    datapoint,
    db,
    tagPath,
    ...(displayName ? { displayLabel: displayName } : {}),
    ...(input.cadenceMs != null ? { frequencyMs: input.cadenceMs } : {}),
    ...(input.enabled != null ? { enabled: input.enabled } : {})
  }
  return { ok: true, registration }
}
